import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Heart, Star, MapPin, ArrowLeft, Trash2 } from 'lucide-react';
import { useFavorites } from '../context/FavoritesContext';
import Breadcrumbs from '../components/Breadcrumbs';
import { api } from '../services/api';
import { resolveBackendImage } from '../utils/brandLogo';


export default function Favoritos() {
  const navigate = useNavigate();
  const { municipioSlug } = useParams();
  const { favorites, removeFavorite } = useFavorites();
  const [municipio, setMunicipio] = useState(null);

  // Load municipality info
  useEffect(() => {
    const loadMunicipio = async () => {
      if (municipioSlug) {
        try {
          const res = await api.get('/municipios');
          const found = res.data.find(m => m.slug === municipioSlug);
          if (found) setMunicipio(found);
        } catch (e) {
          console.error('Error loading municipio:', e);
        }
      }
    };
    loadMunicipio();
  }, [municipioSlug]);

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20">
      {/* Header with Breadcrumbs */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Breadcrumbs 
            items={[
              { label: 'Meus Favoritos', path: null }
            ]}
            municipio={municipio}
            className="mb-2"
          />
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6" />
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                Meus Favoritos
              </h1>
              <p className="text-white/80 text-sm">
                {favorites.length} {favorites.length === 1 ? 'negócio salvo' : 'negócios salvos'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {favorites.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Nenhum favorito ainda</h2>
            <p className="text-[var(--text-secondary)] mb-6">
              Salve negócios que você gosta para acessar rapidamente depois
            </p>
            <button
              onClick={() => navigate('/explorar')}
              className="bg-[var(--primary)] text-white px-6 py-3 rounded-full font-medium hover:opacity-90 transition-opacity"
            >
              Explorar negócios
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {favorites.map((business) => (
              <div
                key={business.id}
                className="bg-white rounded-xl border border-[var(--border)] overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="flex">
                  <div 
                    className="w-32 h-32 bg-gray-100 flex-shrink-0 cursor-pointer"
                    onClick={() => navigate(`/negocio/${business.id}`)}
                  >
                    {business.foto ? (
                      <img
                        src={resolveBackendImage(business.foto)}
                        alt={business.nome}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <MapPin className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 p-4 flex flex-col justify-between">
                    <div>
                      <h3 
                        className="font-semibold text-lg cursor-pointer hover:text-[var(--primary)]"
                        onClick={() => navigate(`/negocio/${business.id}`)}
                      >
                        {business.nome}
                      </h3>
                      <p className="text-sm text-[var(--text-secondary)]">
                        {business.categoria} • {business.bairro}
                      </p>
                      {business.media_avaliacoes && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="text-sm font-medium">
                            {business.media_avaliacoes.toFixed(1)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <button
                        onClick={() => navigate(`/negocio/${business.id}`)}
                        className="text-[var(--primary)] text-sm font-medium hover:underline"
                      >
                        Ver detalhes
                      </button>
                      <button
                        onClick={() => removeFavorite(business.id)}
                        className="text-red-500 hover:bg-red-50 p-2 rounded-full transition-colors"
                        title="Remover dos favoritos"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
